import { Box, Collapse, Paper, Typography } from '@material-ui/core'
import { CustomTooltip } from 'components/facets'
import { useLocalForageState } from 'lib/hooks/useLocalForageState'
import { useStockStore } from 'lib/hooks/useStockStore'
import _ from 'lodash'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { InventoryArticle, Location, RichArticle, Template } from 'shared'
import { SetState } from 'types'
import { ExistingArticle } from './ExistingArticle'

interface ExistingStockPresenterProps {
  article: RichArticle
  setInputs: SetState<RichArticle>
  template: Template
  setDisabledCommentTextfield: SetState<boolean>
}

const getMatchingArticle = (similarArticles: InventoryArticle[]) => {
  const newArticle = _.cloneDeep(similarArticles[0])

  const alreadyExistingLocations: Location[] = []
  similarArticles.forEach((article) => {
    article.locations?.forEach((location) => {
      alreadyExistingLocations.push(location)
    })
  })

  if (alreadyExistingLocations.length > 0) {
    newArticle.locations = alreadyExistingLocations
  }
  return newArticle
}

export const ExistingStockPresenter = ({
  article,
  setInputs,
  template,
  setDisabledCommentTextfield,
}: ExistingStockPresenterProps): JSX.Element => {
  const fetchedStock = useStockStore((s) => s.fetchedStock)

  const { t } = useTranslation()
  const [{ shouldAutofillExactMatch, isActive }, setListingSettings] = useLocalForageState<{
    shouldAutofillExactMatch: boolean
    isActive: boolean
  }>({ shouldAutofillExactMatch: true, isActive: true }, 'listingSettings')

  const fillCommentAndPrice = ({ comment, price }: { comment: string; price: number }): void => {
    const newInputs = article.copy()
    newInputs.setAttribute('price', price)
    newInputs.setAttribute('comment', comment)
    setInputs(newInputs)
  }

  const completeStock = useMemo(
    () => fetchedStock.stock.concat(fetchedStock.sealedStock),
    [fetchedStock.stock, fetchedStock.sealedStock]
  )

  const matchingArticle = useMemo(() => {
    const foundArticles = completeStock.filter(
      (stockArticle) => stockArticle.idProduct === article.idProduct
    )
    const similarArticles = foundArticles.filter((found) => found.isSimilar(article))

    if (similarArticles.length === 0) return null
    return getMatchingArticle(similarArticles)
  }, [completeStock, article])

  useEffect(() => {
    if (!isActive) return

    if (!shouldAutofillExactMatch || !matchingArticle) {
      if (article.comment !== template.comment || article.price !== template.price) {
        fillCommentAndPrice({ comment: template.comment, price: template.price })
      }
      setDisabledCommentTextfield(false)
    } else if (shouldAutofillExactMatch && matchingArticle) {
      if (article.comment !== matchingArticle.comment || article.price !== matchingArticle.price) {
        fillCommentAndPrice(matchingArticle)
        setDisabledCommentTextfield(true)
      }
    }
  }, [shouldAutofillExactMatch, isActive, matchingArticle])

  return (
    <Box>
      <Box>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" alignItems="center" data-testid="expand-similar-articles">
            <Typography
              style={{ cursor: 'pointer' }}
              onClick={() =>
                setListingSettings((settings) => ({ ...settings, isActive: !isActive }))
              }
              variant="h6"
            >
              Similar articles in your{' '}
              <span>
                <CustomTooltip
                  title={
                    <Typography>
                      {t('stockPricing.stockFetchedOn')} <b>{fetchedStock.fetchedAt}.</b>
                      <br />
                      {t('addArticles.existingStock.youCanRefreshIt')}
                    </Typography>
                  }
                >
                  <Typography variant="h6" component="span" color="primary">
                    <b>stock</b>
                  </Typography>
                </CustomTooltip>
              </span>
              :
            </Typography>
          </Box>
        </Box>

        <Collapse in={isActive}>
          {matchingArticle ? (
            <Paper
              style={{
                cursor: 'pointer',
                border: `1px solid blue`,
                maxWidth: '580px',
              }}
              data-testid={`existing-article-${matchingArticle.ptProductId}`}
            >
              <ExistingArticle
                hideTitle
                showComment={true}
                article={matchingArticle}
                locations={matchingArticle.locations ?? []}
              />
            </Paper>
          ) : (
            <Typography color="textSecondary">
              {t('addArticles.existingStock.noneFound')}
            </Typography>
          )}
        </Collapse>
      </Box>
    </Box>
  )
}
